import React, { useState, useEffect } from 'react'
import { Lock, Eye, EyeOff, AlertCircle, CheckCircle, ArrowLeft } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { SuperstateLogo } from '@/components/SuperstateLogo'
import { apiClient } from '@/lib/api'

interface ResetPasswordFormProps {
  token: string
  onResetComplete?: (newPassword: string) => Promise<void>
  onBackToLogin?: () => void
  loading?: boolean
  error?: string
  success?: boolean
  className?: string
}

interface PasswordPolicy {
  min_length: number
  require_capital: boolean
  require_lowercase: boolean
  require_number: boolean
  require_symbol: boolean
  capital_count: number
  lowercase_count: number
  number_count: number
  symbol_count: number
}

export function ResetPasswordForm({
  token: _token,
  onResetComplete,
  onBackToLogin,
  loading = false,
  error,
  success = false,
  className
}: ResetPasswordFormProps) {
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  const [passwordPolicy, setPasswordPolicy] = useState<PasswordPolicy | null>(null)

  useEffect(() => {
    // Fetch password policy on component mount
    const fetchPasswordPolicy = async () => {
      try {
        const policy = await apiClient.get<PasswordPolicy>('/auth/password-policy')
        setPasswordPolicy(policy)
      } catch (err) {
        console.warn('Failed to fetch password policy:', err)
      }
    }
    fetchPasswordPolicy()
  }, [])

  const validatePassword = (password: string) => {
    const errors: string[] = []

    if (!passwordPolicy) {
      // Basic validation if policy not loaded
      if (password.length < 8) errors.push('Password must be at least 8 characters')
      if (!/[A-Z]/.test(password)) errors.push('Password must contain at least 1 capital letter')
      if (!/[a-z]/.test(password)) errors.push('Password must contain at least 1 lowercase letter')
      if (!/[0-9]/.test(password)) errors.push('Password must contain at least 1 number')
      if (!/[^A-Za-z0-9]/.test(password)) errors.push('Password must contain at least 1 symbol')
      return errors
    }

    // Use loaded policy for validation
    if (password.length < passwordPolicy.min_length) {
      errors.push(`Password must be at least ${passwordPolicy.min_length} characters`)
    }

    if (passwordPolicy.require_capital) {
      const capitalCount = (password.match(/[A-Z]/g) || []).length
      if (capitalCount < passwordPolicy.capital_count) {
        errors.push(`Password must contain at least ${passwordPolicy.capital_count} capital letter(s)`)
      }
    }

    if (passwordPolicy.require_lowercase) {
      const lowercaseCount = (password.match(/[a-z]/g) || []).length
      if (lowercaseCount < passwordPolicy.lowercase_count) {
        errors.push(`Password must contain at least ${passwordPolicy.lowercase_count} lowercase letter(s)`)
      }
    }

    if (passwordPolicy.require_number) {
      const numberCount = (password.match(/[0-9]/g) || []).length
      if (numberCount < passwordPolicy.number_count) {
        errors.push(`Password must contain at least ${passwordPolicy.number_count} number(s)`)
      }
    }

    if (passwordPolicy.require_symbol) {
      const symbolCount = (password.match(/[^A-Za-z0-9]/g) || []).length
      if (symbolCount < passwordPolicy.symbol_count) {
        errors.push(`Password must contain at least ${passwordPolicy.symbol_count} symbol(s)`)
      }
    }

    return errors
  }

  const validateForm = () => {
    const errors: Record<string, string> = {}

    if (!password) {
      errors.password = 'Password is required'
    } else {
      const passwordErrors = validatePassword(password)
      if (passwordErrors.length > 0) {
        errors.password = passwordErrors[0] // Show first error
      }
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) return

    if (onResetComplete) {
      try {
        await onResetComplete(password)
      } catch (error) {
        console.error('Password reset failed:', error)
      }
    }
  }

  if (success) {
    return (
      <div className={cn("w-full max-w-md mx-auto", className)}>
        <div className="bg-white shadow-lg rounded-lg p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="mx-auto rounded-full flex items-center justify-center mb-4">
              <SuperstateLogo className="h-[25px] w-[255px]" variant="#0000FF" />
            </div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">Password Reset Complete</h2>
            <p className="text-gray-600">Your password has been successfully updated</p>
          </div>

          {/* Success Message */}
          <Alert className="mb-6 border-green-200 bg-green-50">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800">
              Your password has been updated successfully. You can now sign in with your new password.
            </AlertDescription>
          </Alert>

          {/* Back to Login */}
          <Button
            type="button"
            onClick={onBackToLogin}
            className="w-full"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Continue to Sign In
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className={cn("w-full max-w-md mx-auto", className)}>
      <div className="bg-white shadow-lg rounded-lg p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="mx-auto rounded-full flex items-center justify-center mb-4">
            <SuperstateLogo className="h-[25px] w-[255px]" variant="#0000FF" />
          </div>
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">Reset Password</h2>
          <p className="text-gray-600">Enter your new password below</p>
        </div>

        {/* Error Message */}
        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Password Policy Info */}
        {passwordPolicy && (
          <div className="mb-6 p-3 bg-gray-50 rounded-md text-sm text-gray-600">
            <p className="font-medium mb-1">Password Requirements:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>At least {passwordPolicy.min_length} characters</li>
              {passwordPolicy.require_capital && (
                <li>At least {passwordPolicy.capital_count} capital letter(s)</li>
              )}
              {passwordPolicy.require_lowercase && (
                <li>At least {passwordPolicy.lowercase_count} lowercase letter(s)</li>
              )}
              {passwordPolicy.require_number && (
                <li>At least {passwordPolicy.number_count} number(s)</li>
              )}
              {passwordPolicy.require_symbol && (
                <li>At least {passwordPolicy.symbol_count} symbol(s)</li>
              )}
            </ul>
          </div>
        )}

        {/* Reset Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* New Password Field */}
          <div className="space-y-2">
            <Label htmlFor="password">New Password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={cn(
                  "pl-10 pr-12",
                  formErrors.password && "border-red-300 focus-visible:ring-red-500"
                )}
                placeholder="Enter your new password"
                disabled={loading}
                autoFocus
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-1 top-1/2 transform -translate-y-1/2 h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
            {formErrors.password && (
              <p className="text-sm text-red-600">{formErrors.password}</p>
            )}
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-[#0000FF] hover:bg-[#0000CC] text-white border-0"
          >
            {loading ? (
              <div className="flex items-center justify-center space-x-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Resetting Password...</span>
              </div>
            ) : (
              <div className="flex items-center justify-center space-x-2">
                <Lock className="h-4 w-4" />
                <span>Reset Password</span>
              </div>
            )}
          </Button>
        </form>

        {/* Back to Login Link */}
        <div className="mt-6 text-center">
          <Button
            type="button"
            variant="link"
            onClick={onBackToLogin}
            className="p-0 h-auto font-medium text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Sign In
          </Button>
        </div>
      </div>
    </div>
  )
}
